import type { GaVueComponent } from "@/common/vueUtils";
import type {
  ContentId_ACTIONABLE,
  ContentId_MIAB,
  ContentId_SUMMARY,
  ContentIxStatus_ACTIONABLE,
  ContentIxStatus_MIAB,
  ContentIxStatus_SUMMARY,
  ContentMetadata_ACTIONABLE,
  ContentMetadata_MIAB,
  ContentMetadata_SUMMARY,
  ContentMetadataView,
  FormFilter,
  FormSorting,
  PageInfo,
} from "@newgenerated/shared/schema";
import { ContentFilteringSorting, type ContentTypeFilterProps, type FormSortingExtended, toFormFilterExtended } from "@/components/sorting-filtering-paging/ContentSortingFiltering";
import type { Store } from "@/common/storeUtils";
import { DynamicGrid } from "@/components/cards/DynamicGrid";
import { ContentCard, type TranslationFnAsync } from "@/components/cards/ContentCard";
import type { DeepReadonly } from "vue";
import { NoResults } from "@/components/sorting-filtering-paging/NoResults";
import { GaButton } from "@/components/general/GaButton";
import { useGaContext } from "@utils/vue-migration/common/gaContext/gaContext";
import { enhanceWithOriginPosition } from "@/common/urlUtils";

type ReplaceFormFilterAndSortingWithExtended<T> = {
  [K in keyof T]: T[K] extends { filter: FormFilter[]; sorting: FormSorting }
    ? Omit<T[K], "filter" | "sorting"> & { filter: ContentTypeFilterProps; sorting: FormSortingExtended }
    : T[K] extends { filter: FormFilter[] }
      ? Omit<T[K], "filter"> & { filter: ContentTypeFilterProps }
      : T[K];
};

export type ContentMetadataViewExtended = ReplaceFormFilterAndSortingWithExtended<ContentMetadataView>;

export const convertContentMetadataView = (view: ContentMetadataView): ContentMetadataViewExtended => {
  return {
    actionable: {
      items: view.actionable.items,
      pageInfo: view.actionable.pageInfo,
      filter: {
        formFilters: toFormFilterExtended(view.actionable.filter),
        formFilterExtended: false,
      },
    },
    summary: {
      items: view.summary.items,
      pageInfo: view.summary.pageInfo,
      filter: {
        formFilters: toFormFilterExtended(view.summary.filter),
        formFilterExtended: false,
      },
      sorting: {
        formSorting: view.summary.sorting,
        collapsed: true,
      },
    },
    miab: {
      items: view.miab.items,
      pageInfo: view.miab.pageInfo,
      filter: {
        formFilters: toFormFilterExtended(view.miab.filter),
        formFilterExtended: false,
      },
    },
  };
};

export const Pagination = (props: { store: Store<PageInfo>; applyFilters: () => void }): GaVueComponent | null => {
  const { t } = useGaContext();
  const hasMore = (pageInfo: PageInfo): boolean => {
    return pageInfo.totalCount > pageInfo.pageSize;
  };

  if (hasMore(props.store.get())) {
    return (
      <div class="d-flex justify-content-center mt-3">
        <GaButton
          onClick={() => {
            props.store.sub("pageSize").set(props.store.sub("pageSize").get() + 12);
            props.applyFilters();
          }}>
          {t("browse:explore.loadMore", ["12"])}
        </GaButton>
      </div>
    );
  } else {
    return null;
  }
};

export const ContentMetadataLoading = (): GaVueComponent => {
  return (
    <div class="placeholder-wave mb-5">
      <DynamicGrid>
        <div style={{ height: "172px" }} class="placeholder"></div>
        <div style={{ height: "172px" }} class="placeholder"></div>
        <div style={{ height: "172px" }} class="placeholder"></div>
        <div style={{ height: "172px" }} class="placeholder"></div>
      </DynamicGrid>
    </div>
  );
};

export const ContentMetadataError = (props: { retry?: () => void }): GaVueComponent => {
  const { t } = useGaContext();
  return (
    <div class="alert alert-dark d-flex flex-column gap-5 justify-content-center" style={{ height: "172px" }} role="alert">
      <span class={"text-center"}>{t("error.500.title", "Oops... Something went wrong.")}</span>
      {props.retry === undefined ? null : (
        <div class="d-flex justify-content-center">
          <GaButton variant={"primary-alt"} onClick={props.retry}>
            <i class="ico-rotate-cw me-2" />
            {t("general:retry", "retry")}
          </GaButton>
        </div>
      )}
    </div>
  );
};

export const ContentMetadataGridView = <T extends ContentMetadata_SUMMARY | ContentMetadata_ACTIONABLE | ContentMetadata_MIAB>(props: {
  filteringAndSorting?: { filterStore: Store<ContentTypeFilterProps>; sortingStore?: Store<FormSortingExtended>; pageInfo: Store<PageInfo>; apply: () => void };
  onBookmark: (content: DeepReadonly<T>) => void;
  translationFn: Store<TranslationFnAsync>;
  trackingParams?: URLSearchParams;
  items: DeepReadonly<T[]>;
  status: "LOADING" | "ERROR" | "READY";
  ixStatus: DeepReadonly<
    T extends ContentMetadata_SUMMARY
      ? Map<ContentId_SUMMARY["id"], ContentIxStatus_SUMMARY>
      : T extends ContentMetadata_ACTIONABLE
        ? Map<ContentId_ACTIONABLE["id"], ContentIxStatus_ACTIONABLE>
        : Map<ContentId_MIAB["id"], ContentIxStatus_MIAB>
  >;
}): GaVueComponent => {
  const renderContent = (): GaVueComponent => {
    switch (props.status) {
      case "LOADING":
        return <ContentMetadataLoading />;
      case "ERROR":
        return <ContentMetadataError retry={props.filteringAndSorting?.apply} />;
      case "READY":
        return props.items.length > 0 ? (
          <>
            <DynamicGrid>
              {props.items.map((content, index) => (
                <ContentCard
                  translationFn={props.translationFn}
                  additionalParams={enhanceWithOriginPosition(new URLSearchParams(props.trackingParams), props.filteringAndSorting?.pageInfo.get() ?? { page: 0, pageSize: props.items.length, totalCount: props.items.length }, index)}
                  content={content}
                  onBookmark={() => props.onBookmark(content)}
                  layout={"horizontal"}
                  ixStatus={props.ixStatus.get(content.contentId.id)}
                />
              ))}
            </DynamicGrid>
            {props.filteringAndSorting === undefined ? null : <Pagination store={props.filteringAndSorting.pageInfo} applyFilters={props.filteringAndSorting.apply} />}
          </>
        ) : (
          <NoResults />
        );
    }
  };

  return (
    <>
      {props.filteringAndSorting === undefined ? null : <ContentFilteringSorting filterStore={props.filteringAndSorting.filterStore} sortingStore={props.filteringAndSorting.sortingStore} apply={props.filteringAndSorting.apply} />}
      {renderContent()}
    </>
  );
};
